import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Youtube } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { api } from "@/lib/api";
import { fadeUp, stagger } from "@/lib/animations";
import { SOCIALS } from "@/lib/social";
import type { Video, VideoSort, VideosResponse } from "@/lib/videos";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import TikTokIcon from "@/components/ui/TikTokIcon";
import VideoCard from "@/components/videos/VideoCard";

// Curated set shown on-site. Beyond this, viewers are sent to her channels.
const CURATED_LIMIT = 12;

const SORTS: { key: VideoSort; label: string }[] = [
  { key: "recent", label: "Recent" },
  { key: "views", label: "Most viewed" },
];

export function Videos() {
  const theme = usePalette();
  const [videos, setVideos] = useState<Video[]>([]);
  const [sort, setSort] = useState<VideoSort>("recent");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<VideosResponse>(`/api/videos?page=1&limit=${CURATED_LIMIT}&sort=${sort}`)
      .then((r) => {
        setVideos(r.videos);
        setErr(null);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Failed to load videos"))
      .finally(() => setLoading(false));
  }, [sort]);

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-12" style={{ backgroundColor: colors.cream }}>
        <Blob className="w-96 h-96 -top-20 -left-10" color={theme.blob1} />
        <Blob className="w-72 h-72 top-10 right-0" color={theme.blob3} />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="relative z-10 mx-auto max-w-5xl px-6 text-center"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="mb-3 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: theme.accent,
            }}
          >
            Watch along
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-5xl sm:text-6xl lg:text-7xl"
            style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy, lineHeight: 1.1 }}
          >
            The{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>videos</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mx-auto mt-6 max-w-2xl text-base"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}b3`,
              letterSpacing: "0.03em",
              lineHeight: 1.7,
            }}
          >
            A handful of favourites — sewing projects, hauls, and behind-the-scenes. For the
            full feed, come find me on YouTube and Instagram.
          </motion.p>

          {/* Sort toggle */}
          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-8 inline-flex rounded-full p-1"
            style={{ backgroundColor: `${theme.accent}14`, border: `1px solid ${theme.accent}26` }}
          >
            {SORTS.map((s) => {
              const active = sort === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSort(s.key)}
                  className="px-5 py-2 rounded-full transition-all duration-200"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    backgroundColor: active ? theme.accent : "transparent",
                    color: active ? "white" : theme.accent,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      {/* Grid */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-6xl">
          {err && (
            <p className="text-center" style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}>
              {err}
            </p>
          )}

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden border"
                  style={{ backgroundColor: "white", borderColor: `${colors.tan}25` }}
                >
                  <div className="w-full aspect-video animate-pulse" style={{ backgroundColor: `${theme.accent}14` }} />
                  <div className="p-5">
                    <div className="h-4 rounded animate-pulse mb-2" style={{ backgroundColor: `${colors.navy}10`, width: "90%" }} />
                    <div className="h-3 rounded animate-pulse" style={{ backgroundColor: `${colors.navy}10`, width: "55%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div
              className="rounded-3xl p-12 text-center"
              style={{
                backgroundColor: theme.sectionBg,
                border: `1px dashed ${colors.tan}55`,
                fontFamily: fonts.body,
                color: `${colors.navy}aa`,
              }}
            >
              No videos yet — check back soon.
            </div>
          ) : (
            <motion.div
              key={sort}
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {videos.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* See more — out to her channels */}
      <section className="px-6 pb-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mx-auto max-w-3xl rounded-3xl px-8 py-12 text-center"
          style={{ backgroundColor: theme.sectionBg, border: `1px solid ${colors.tan}25` }}
        >
          <motion.h2
            variants={fadeUp}
            custom={0}
            className="text-3xl lg:text-4xl mb-3"
            style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy }}
          >
            Want{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>more?</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mx-auto mb-8 max-w-md text-sm"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}99`,
              letterSpacing: "0.03em",
              lineHeight: 1.7,
            }}
          >
            New videos drop all the time. Follow along on your favourite platform for the
            full library.
          </motion.p>
          <motion.div
            variants={fadeUp}
            custom={2}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              href={SOCIALS.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                backgroundColor: theme.accent,
              }}
            >
              <Youtube size={18} />
              See more on YouTube
            </a>
            <a
              href={SOCIALS.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 text-sm transition-all duration-200 hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                borderColor: `${colors.navy}20`,
                color: colors.navy,
              }}
            >
              <Instagram size={18} />
              Follow on Instagram
            </a>
            <a
              href={SOCIALS.tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 text-sm transition-all duration-200 hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                borderColor: `${colors.navy}20`,
                color: colors.navy,
              }}
            >
              <TikTokIcon className="w-[18px] h-[18px]" />
              TikTok
            </a>
          </motion.div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
